import React from 'react';

import isEmpty from 'lodash/isEmpty';

import { useNotification } from '@lumapps/notifications/hooks/useNotifications';
import { GLOBAL, useTranslate } from '@lumapps/translations';
import { fromMarkdown } from '@lumapps/wrex-core/serialization/markdown/fromMarkdown';
import { useSlate, ReactEditor, Transforms, BaseSelection, Range } from '@lumapps/wrex-core/slate';

import { getCompletions } from '../api';
import { WREX_GENERATED_CONTENT } from '../keys';

export interface UseRefineContentOutput {
    newSuggestions?: string[];
    isRefiningContent: boolean;
    isPopoverOpen: boolean;
    onRefineContent: (
        refinement: string,
        selectedText?: string,
        tone?: string,
        isPromptRefined?: boolean,
    ) => Promise<void>;
    onInsertNewContent: (currentSelection?: BaseSelection, index?: number) => void;
    onCancelRefinement: () => void;
    setNewSuggestions: React.Dispatch<React.SetStateAction<string[] | undefined>>;
    setIsPopoverOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

export const useRefineContentDialog = (): UseRefineContentOutput => {
    const editor = useSlate() as ReactEditor;

    const { translateAndReplace, translateKey } = useTranslate();

    const [isRefiningContent, setIsRefiningContent] = React.useState(false);
    const [newSuggestions, setNewSuggestions] = React.useState<string[] | undefined>(undefined);
    const [isPopoverOpen, setIsPopoverOpen] = React.useState(false);

    const { error } = useNotification();

    const onRefineContent = async (
        refinement: string,
        selectedText?: string,
        tone?: string,
        isPromptRefined: boolean = false,
    ) => {
        if (!selectedText) {
            error({ translate: GLOBAL.GENERIC_ERROR });
            return;
        }

        setIsRefiningContent(true);
        setIsPopoverOpen(true);

        // For instance, build a new prompt which starts with "Make this sentence more professionnal..."
        const getPromptTone = (tone: string) =>
            `${translateAndReplace(WREX_GENERATED_CONTENT.PROMPT_REFINE_TONE, {
                TYPE: translateKey(tone)?.toLowerCase(),
                SELECTED_TEXT: selectedText,
            })}`;

        // Compute new prompt
        const newPrompt = () => {
            // Custom refined prompt from the text input in UpdateContentDialog
            if (isPromptRefined) {
                return `${refinement} : ${selectedText}`;
            }
            // Refined prompt using the UpdateContentMenu options
            return tone ? getPromptTone(tone) : translateAndReplace(refinement, { SELECTED_TEXT: selectedText });
        };

        try {
            const { data } = await getCompletions({ prompt: newPrompt() });
            setNewSuggestions((prev) => (prev ? [...prev, data.completions] : [data.completions]));
        } catch (e) {
            error({ translate: GLOBAL.GENERIC_ERROR });
            setIsPopoverOpen(false);
        }

        setIsRefiningContent(false);
    };

    const onInsertNewContent = (currentSelection?: BaseSelection, index?: number) => {
        if (newSuggestions && !isEmpty(newSuggestions) && currentSelection && index !== undefined) {
            Transforms.insertNodes(editor, fromMarkdown(newSuggestions[index]), {
                at: currentSelection,
                select: true,
            });

            // Starting point of the selection depending on which comes first in the document order
            const startPoint = Range.start(currentSelection);

            // Re-focus in the editor to select content
            ReactEditor.focus(editor);

            if (editor.selection) {
                const endPoint = Range.end(editor?.selection);

                // Force select the new inserted paragraphs
                Transforms.select(editor, {
                    anchor: startPoint,
                    focus: endPoint,
                });
            }
        } else {
            error({ translate: GLOBAL.GENERIC_ERROR });
        }

        setIsPopoverOpen(false);
    };

    const onCancelRefinement = () => {
        setNewSuggestions(undefined);
        setIsPopoverOpen(false);
    };

    return {
        newSuggestions,
        isRefiningContent,
        isPopoverOpen,
        onRefineContent,
        onInsertNewContent,
        onCancelRefinement,
        setNewSuggestions,
        setIsPopoverOpen,
    };
};
